import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Box,
    Divider,
    Chip,
    Paper,
    Avatar,
    Fade,
    IconButton
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EuroIcon from '@mui/icons-material/Euro';
import { Enrollment } from '../../types/enrollment';
import { useAuth } from '../../context/AuthContext';
import { addDays, addMonths, format } from 'date-fns';

interface EnrollmentDetailsProps {
    open: boolean;
    onClose: () => void;
    enrollment: Enrollment | null;
}

const EnrollmentDetails: React.FC<EnrollmentDetailsProps> = ({ open, onClose, enrollment }) => {
    const { user } = useAuth();
    if (!enrollment) return null;

    const offerPrice = enrollment.offer?.price || 0;
    const expectedDeposit = enrollment.expectedDepositAmount !== undefined ? enrollment.expectedDepositAmount : Math.round(offerPrice * 0.3);
    const expectedTotal = enrollment.expectedTotalAmount !== undefined ? enrollment.expectedTotalAmount : Math.round(offerPrice * 0.7);

    const enrollmentDate = enrollment.createdAt ? new Date(enrollment.createdAt) : new Date();
    const depositExpiry = enrollment.depositExpiryDate ? new Date(enrollment.depositExpiryDate) : addDays(enrollmentDate, 31);
    const totalExpiry = enrollment.totalExpiryDate ? new Date(enrollment.totalExpiryDate) : addMonths(enrollmentDate, 9);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }
            }}
        >
            <Box sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                p: 3,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 2
            }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <SchoolIcon />
                </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight={700}>Détails de l'inscription</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        ID: {enrollment._id}
                    </Typography>
                </Box>
            </Box>

            <DialogContent sx={{ p: 4, bgcolor: '#f8f9fa' }}>
                <Grid container spacing={3}>
                    {/* Learner Info */}
                    <Grid size={{ md: 6 }}>
                        <Paper sx={{ p: 3, height: '100%', borderRadius: 3 }} elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#ebf4ff', color: '#667eea' }}>
                                    <PersonIcon />
                                </Avatar>
                                <Typography variant="subtitle1" fontWeight={700}>Apprenant</Typography>
                            </Box>
                            <Box sx={{ pl: 7 }}>
                                <Typography variant="body1" fontWeight={600}>
                                    {enrollment.learner?.firstName} {enrollment.learner?.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {enrollment.learner?.email}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Course Info */}
                    <Grid size={{ md: 6 }}>
                        <Paper sx={{ p: 3, height: '100%', borderRadius: 3 }} elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#ebf4ff', color: '#667eea' }}>
                                    <SchoolIcon />
                                </Avatar>
                                <Typography variant="subtitle1" fontWeight={700}>Formation</Typography>
                            </Box>
                            <Box sx={{ pl: 7 }}>
                                <Typography variant="body1" fontWeight={600}>
                                    {enrollment.parcours?.title}
                                </Typography>
                                <Chip
                                    label={enrollment.status === 'active' ? 'Actif' : enrollment.status === 'pending' ? 'En attente' : enrollment.status}
                                    size="small"
                                    color={enrollment.status === 'active' ? 'success' : 'default'}
                                    sx={{ mt: 1, fontWeight: 600 }}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Dates */}
                    <Grid size={{ md: 6 }}>
                        <Paper sx={{ p: 3, height: '100%', borderRadius: 3 }} elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#fff5f5', color: '#e53e3e' }}>
                                    <CalendarTodayIcon />
                                </Avatar>
                                <Typography variant="subtitle1" fontWeight={700}>Dates</Typography>
                            </Box>
                            <Box sx={{ pl: 7 }}>
                                <Typography variant="body2" color="text.secondary">Du</Typography>
                                <Typography variant="body1" fontWeight={500}>{new Date(enrollment.startDate).toLocaleDateString('fr-FR')}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Au</Typography>
                                <Typography variant="body1" fontWeight={500}>{new Date(enrollment.endDate).toLocaleDateString('fr-FR')}</Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Finance */}
                    <Grid size={{ md: 6 }}>
                        <Paper sx={{ p: 3, height: '100%', borderRadius: 3 }} elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#f0fff4', color: '#38a169' }}>
                                    <EuroIcon />
                                </Avatar>
                                <Typography variant="subtitle1" fontWeight={700}>Paiement</Typography>
                            </Box>
                            <Box sx={{ pl: 7 }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="body2" color="text.secondary">Acompte Payé</Typography>
                                        <Typography variant="body1" fontWeight={600} color={enrollment.depositPaid >= expectedDeposit ? 'success.main' : 'text.primary'}>
                                            {enrollment.depositPaid} €
                                        </Typography>
                                        <Typography variant="caption" display="block" color="primary.main" sx={{ mt: 0.5 }}>
                                            Attendu : {expectedDeposit}€
                                        </Typography>
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            Échéance : {format(depositExpiry, 'dd/MM/yyyy')}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="body2" color="text.secondary">Total Payé</Typography>
                                        <Typography variant="body1" fontWeight={600} color={enrollment.totalPaid >= expectedTotal ? 'success.main' : 'text.primary'}>
                                            {enrollment.totalPaid} €
                                        </Typography>
                                        <Typography variant="caption" display="block" color="primary.main" sx={{ mt: 0.5 }}>
                                            Attendu : {expectedTotal}€
                                        </Typography>
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            Échéance : {format(totalExpiry, 'dd/MM/yyyy')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Invoices (OF and Admin only) */}
                    {(user?.role === 'super-admin' || user?.role === 'of-admin') && (enrollment.depositInvoice || enrollment.finalInvoice) && (
                        <Grid size={{ xs: 12 }}>
                            <Paper sx={{ p: 3, borderRadius: 3 }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Avatar sx={{ bgcolor: '#ede7f6', color: '#673ab7' }}>
                                        <ReceiptIcon />
                                    </Avatar>
                                    <Typography variant="subtitle1" fontWeight={700}>Factures rattachées</Typography>
                                </Box>
                                <Box sx={{ pl: 7 }}>
                                    <Grid container spacing={2}>
                                        {enrollment.depositInvoice && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography variant="body2" color="text.secondary">Facture Acompte</Typography>
                                                <Button 
                                                    size="small" 
                                                    variant="outlined" 
                                                    startIcon={<LinkIcon />} 
                                                    href={enrollment.depositInvoice} 
                                                    target="_blank"
                                                    sx={{ mt: 1, borderRadius: 2 }}
                                                >
                                                    Visualiser
                                                </Button>
                                            </Grid>
                                        )}
                                        {enrollment.finalInvoice && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography variant="body2" color="text.secondary">Facture Finale</Typography>
                                                <Button 
                                                    size="small" 
                                                    variant="outlined" 
                                                    startIcon={<LinkIcon />} 
                                                    href={enrollment.finalInvoice} 
                                                    target="_blank"
                                                    sx={{ mt: 1, borderRadius: 2 }}
                                                >
                                                    Visualiser
                                                </Button>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </Paper>
                        </Grid>
                    )}

                    {/* Postponements */}
                    {enrollment.postponements && enrollment.postponements.length > 0 && (
                        <Grid size={{ xs: 12 }}>
                            <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#fffaf0', border: '1px solid #fbd38d' }} elevation={0}>
                                <Typography variant="subtitle2" sx={{ color: '#c05621', fontWeight: 700, mb: 2, textTransform: 'uppercase' }}>
                                    Historique des reports
                                </Typography>
                                {enrollment.postponements.map((p, index) => (
                                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < enrollment.postponements!.length - 1 ? '1px dashed #cbd5e0' : 'none' }}>
                                        <Grid container>
                                            <Grid size={{ xs: 4 }}>
                                                <Typography variant="caption" color="text.secondary">Date de demande</Typography>
                                                <Typography variant="body2" fontWeight={600}>{new Date(p.requestDate).toLocaleDateString('fr-FR')}</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 4 }}>
                                                <Typography variant="caption" color="text.secondary">Nouvelle fin</Typography>
                                                <Typography variant="body2" fontWeight={600}>{new Date(p.newEndDate).toLocaleDateString('fr-FR')}</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="text.secondary">Raison</Typography>
                                                <Typography variant="body2">{p.reason}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Button onClick={onClose} variant="contained" sx={{
                    bgcolor: 'white',
                    color: '#4a5568',
                    boxShadow: 'none',
                    border: '1px solid #cbd5e0',
                    '&:hover': { bgcolor: '#f7fafc' }
                }}>
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EnrollmentDetails;
